import type {
  EvalSingletonResponse,
  EvalsResultOpensearchLog,
  PassFailResponse,
  ReferenceOutputResponse,
  ScoreResponse,
} from '@/types/eval-v2-types';
import type { EvalNodeInfo } from '@/pages/workflow/component/controls/evals-tab-content';

// ---------------------------------------------------------------------------
// Shape detection (mirrors eval-results-mapper.ts)
// ---------------------------------------------------------------------------

const REFERENCE_OUTPUT_META_KEYS = new Set([
  'overall_score',
  'overall_explanation',
  'error',
  'missing_elements',
  'added_elements',
]);

function isReferenceOutputResponse(
  r: EvalSingletonResponse,
): r is ReferenceOutputResponse {
  return (
    typeof (r as ReferenceOutputResponse)?.overall_score === 'number' &&
    typeof (r as ReferenceOutputResponse)?.overall_explanation === 'string'
  );
}

function isDimensionScore(
  v: unknown,
): v is { score: number; explanation: string } {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { score: unknown }).score === 'number' &&
    typeof (v as { explanation: unknown }).explanation === 'string'
  );
}

function isPassFailResponse(r: EvalSingletonResponse): r is PassFailResponse {
  const verdict = (r as PassFailResponse)?.verdict;
  return (
    typeof verdict === 'string' &&
    (verdict === 'PASS' || verdict === 'FAIL' || verdict === 'NA')
  );
}

function isScoreResponse(r: EvalSingletonResponse): r is ScoreResponse {
  return (
    typeof (r as ScoreResponse)?.score === 'number' &&
    typeof (r as ScoreResponse)?.explanation === 'string' &&
    !isReferenceOutputResponse(r)
  );
}

function humanize(name: string): string {
  return name.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// CSV helpers
// ---------------------------------------------------------------------------

function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function csvRow(cells: string[]): string {
  return cells.map((c) => escapeCSV(c)).join(',');
}

// ---------------------------------------------------------------------------
// Row builders — one row per eval×input×dimension
// ---------------------------------------------------------------------------

interface FlatRow {
  nodeName: string;
  nodeId: string;
  upstreamNode: string;
  evalName: string;
  metric: string;
  result: string;
  score: string;
  explanation: string;
  runDate: string;
}

function flattenRecord(
  rec: EvalsResultOpensearchLog,
  nodeLookup: Map<string, string>,
  upstreamLookup: Map<string, string[]>,
): FlatRow[] {
  const rows: FlatRow[] = [];
  const nodeName = nodeLookup.get(rec.node_id ?? '') ?? rec.name;
  const nodeId = rec.node_id ?? '';
  // Comma-separated when an Evals node has multiple upstream connections;
  // empty string when the node was orphaned at run time, when the canvas
  // node has been deleted, or when the record predates the upstream
  // tracking. Kept as a single string column so it doesn't break
  // downstream CSV parsers that expect a stable column count.
  const upstreamNode = (upstreamLookup.get(nodeId) ?? []).join(', ');
  const runDate = rec.run_date ?? rec.created_at ?? '';

  for (const evalLog of rec.eval_results ?? []) {
    if (evalLog.error_msg) {
      rows.push({
        nodeName,
        nodeId,
        upstreamNode,
        evalName: humanize(evalLog.eval_name),
        metric: '',
        result: 'ERROR',
        score: '',
        explanation: evalLog.error_msg,
        runDate,
      });
      continue;
    }

    for (const input of evalLog.input_results ?? []) {
      const resp = input.response;
      if (!resp || typeof resp !== 'object') continue;

      if (isReferenceOutputResponse(resp)) {
        const evalName = humanize(evalLog.eval_name);
        for (const key of Object.keys(resp)) {
          if (REFERENCE_OUTPUT_META_KEYS.has(key)) continue;
          const val = resp[key];
          if (!isDimensionScore(val)) continue;
          rows.push({
            nodeName,
            nodeId,
            upstreamNode,
            evalName,
            metric: humanize(key),
            result: `${val.score} out of 10`,
            score: String(val.score),
            explanation: val.explanation ?? '',
            runDate,
          });
        }
        rows.push({
          nodeName,
          nodeId,
          upstreamNode,
          evalName,
          metric: 'Overall',
          result: `${resp.overall_score} out of 10`,
          score: String(resp.overall_score),
          explanation: resp.overall_explanation ?? '',
          runDate,
        });
      } else if (isPassFailResponse(resp)) {
        rows.push({
          nodeName,
          nodeId,
          upstreamNode,
          evalName: humanize(evalLog.eval_name),
          metric: 'Verdict',
          result: resp.verdict,
          score: '',
          explanation: resp.explanation ?? '',
          runDate,
        });
      } else if (isScoreResponse(resp)) {
        rows.push({
          nodeName,
          nodeId,
          upstreamNode,
          evalName: humanize(evalLog.eval_name),
          metric: 'Score',
          result: String(resp.score),
          score: String(resp.score),
          explanation: resp.explanation ?? '',
          runDate,
        });
      } else {
        rows.push({
          nodeName,
          nodeId,
          upstreamNode,
          evalName: humanize(evalLog.eval_name),
          metric: '',
          result: JSON.stringify(resp),
          score: '',
          explanation: '',
          runDate,
        });
      }
    }
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Aggregate (Sum / Average) trailer rows
// ---------------------------------------------------------------------------

interface ScoreAggregate {
  count: number;
  sum: number;
  average: number;
}

// Sum across rows whose `score` column parses as a finite number. Pass/Fail
// rows (score === '') and verdicts that didn't produce a numeric value are
// not counted in either the numerator or the denominator — matches the
// in-app spreadsheet behavior in TestResultTable.
export function aggregateScores(rows: FlatRow[]): ScoreAggregate {
  let count = 0;
  let sum = 0;
  for (const row of rows) {
    if (row.score === '') continue;
    const n = Number(row.score);
    if (Number.isFinite(n)) {
      count += 1;
      sum += n;
    }
  }
  return { count, sum, average: count > 0 ? sum / count : 0 };
}

// 4 sig figs is enough for typical 0–10 / 0–1 scoring schemes without
// flooding the cell, and trims trailing zeros for clean display.
function formatScoreAggregate(value: number): string {
  if (!Number.isFinite(value)) return '';
  if (Number.isInteger(value)) return String(value);
  return Number(value.toPrecision(4)).toString();
}

// Build the two trailer rows ("Sum" and "Average") that go beneath the data
// rows in the exported CSV. Returns [] when there are no numeric scores at
// all so the report stays clean for verdict-only / error-only exports.
function buildAggregateRows(rows: FlatRow[]): FlatRow[] {
  const agg = aggregateScores(rows);
  if (agg.count === 0) return [];
  const blank = (label: string, value: number): FlatRow => ({
    nodeName: '',
    nodeId: '',
    upstreamNode: '',
    evalName: '',
    metric: label,
    result: '',
    score: formatScoreAggregate(value),
    explanation:
      agg.count === 1 ? '1 numeric score' : `${agg.count} numeric scores`,
    runDate: '',
  });
  return [blank('Sum', agg.sum), blank('Average', agg.average)];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

// Order: keep the existing eval-node identifiers together (nodeName +
// nodeId) and put the new upstreamNode column right after them, so the
// reader can see "Evals node X (id Y) is grading upstream node Z" in
// one glance.
const HEADERS: (keyof FlatRow)[] = [
  'nodeName',
  'nodeId',
  'upstreamNode',
  'evalName',
  'metric',
  'result',
  'score',
  'explanation',
  'runDate',
];

const HEADER_LABELS: Record<keyof FlatRow, string> = {
  nodeName: 'Node Name',
  nodeId: 'Node ID',
  upstreamNode: 'Upstream Node',
  evalName: 'Eval Name',
  metric: 'Metric',
  result: 'Result',
  score: 'Score',
  explanation: 'Explanation',
  runDate: 'Run Date',
};

// Build the full CSV string (header row + data rows + Sum/Average trailer
// rows). Returns '' when there is no data to export. Exposed for unit tests.
export function buildEvalReportCSV(
  records: EvalsResultOpensearchLog[],
  evalNodes: EvalNodeInfo[],
): string {
  const nodeLookup = new Map(evalNodes.map((n) => [n.nodeId, n.name]));
  // Map eval-node id -> ordered list of upstream node display names.
  // Built once per export so flattenRecord's per-row lookup is O(1).
  const upstreamLookup = new Map(
    evalNodes.map((n) => [n.nodeId, n.upstreamNodeNames]),
  );

  const sorted = [...records].sort((a, b) =>
    (a.node_id ?? '').localeCompare(b.node_id ?? ''),
  );

  const dataRows = sorted.flatMap((rec) =>
    flattenRecord(rec, nodeLookup, upstreamLookup),
  );
  if (dataRows.length === 0) return '';

  const aggregateRows = buildAggregateRows(dataRows);

  const lines: string[] = [
    csvRow(HEADERS.map((h) => HEADER_LABELS[h])),
    ...dataRows.map((row) => csvRow(HEADERS.map((h) => row[h]))),
    ...aggregateRows.map((row) => csvRow(HEADERS.map((h) => row[h]))),
  ];

  return lines.join('\r\n');
}

export function downloadEvalReportCSV(
  records: EvalsResultOpensearchLog[],
  evalNodes: EvalNodeInfo[],
  filename = 'eval-report.csv',
): void {
  const csv = buildEvalReportCSV(records, evalNodes);
  if (!csv) return;

  const blob = new Blob(['\uFEFF' + csv], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
